import React, {useEffect, useMemo, useRef, useState} from 'react';
import TextInput from 'app/Components/Shared/TextInput/TextInput';

const MIN_SEARCH_LENGTH = 2;

export const getBranchLabel = (branch) => {
  if (!branch) {
    return '';
  }
  const branchCode = String(branch.branchCode ?? '').trim();
  const description = String(branch.branchDescriptionBranch ?? '').trim();
  return description ? `${branchCode} ${description}` : branchCode;
};

export const matchesBranchSearch = (branch, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length < MIN_SEARCH_LENGTH) {
    return false;
  }
  const branchCode = String(branch.branchCode ?? '').toLowerCase();
  const description = String(branch.branchDescriptionBranch ?? '').toLowerCase();
  return branchCode.includes(normalizedQuery) || description.includes(normalizedQuery);
};

const BranchAutocompleteInput = ({placeholder, required, disabled, suggestions = [], value, onSelect, onClear}) => {
  const containerRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const filteredSuggestions = useMemo(() => {
    if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
      return [];
    }
    return suggestions.filter((branch) => matchesBranchSearch(branch, inputValue));
  }, [inputValue, suggestions]);

  useEffect(() => {
    if (!value) {
      setInputValue('');
      return;
    }
    const selectedBranch = suggestions.find((branch) => branch.branchCode == value);
    if (selectedBranch) {
      setInputValue(getBranchLabel(selectedBranch));
    }
  }, [value, suggestions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    setActiveIndex(0);
    setShowSuggestions(true);

    if (!nextValue) {
      onClear?.();
      return;
    }

    if (value) {
      const selectedBranch = suggestions.find((branch) => branch.branchCode == value);
      if (!selectedBranch || getBranchLabel(selectedBranch) !== nextValue) {
        onClear?.();
      }
    }
  };

  const handleSelect = (branch) => {
    setInputValue(getBranchLabel(branch));
    setShowSuggestions(false);
    onSelect(branch);
  };

  const handleKeyDown = (event) => {
    if (!showSuggestions || !filteredSuggestions.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, filteredSuggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      handleSelect(filteredSuggestions[activeIndex]);
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const shouldShowSuggestions = showSuggestions && inputValue.trim().length >= MIN_SEARCH_LENGTH && filteredSuggestions.length > 0;

  return (
    <div className='branch-autocomplete' ref={containerRef}>
      <TextInput
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        value={inputValue}
        autoComplete='off'
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
      />

      {shouldShowSuggestions && (
        <ul className='branch-autocomplete__suggestions'>
          {filteredSuggestions.map((branch, index) => (
            <li
              key={`${branch.branchCode}-${index}`}
              className={`branch-autocomplete__suggestion ${index === activeIndex ? 'branch-autocomplete__suggestion--active' : ''}`}
              onMouseDown={(event) => {
                event.preventDefault();
                handleSelect(branch);
              }}>
              {getBranchLabel(branch)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BranchAutocompleteInput;
